/**
 * monographSurfaceVars — generate a monograph demo's scoped surface CSS
 * variables from a single accent + dark-surface ramp.
 *
 * Every monograph hub demo declares the same structural set of scoped CSS
 * custom properties — `--<prefix>-void / surface / elevated / border / muted /
 * text / bright / accent` — so its panels can reach for semantic Tailwind
 * arbitrary-value utilities backed by `var(--<prefix>-surface)` /
 * `var(--<prefix>-text)` instead of inline styles. Twenty-plus demos hand-copy
 * that block into their `globals.css`, each spelling the same eight names.
 *
 * This helper is the shared source of that block. A demo passes its palette
 * once and receives the `--<prefix>-*` declarations as a style object to hand
 * to `MonographDemoShell`'s `surfaceTokens` prop, which emits them on the same
 * root element the demo's `[data-monograph-demo]` selector used to target — so
 * the scoped variables resolve identically, with no per-demo CSS block to keep
 * in sync.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type React from "react";

/** The eight canonical surface roles a monograph demo tints itself with. */
export interface MonographSurfaceScale {
  /** Page backdrop — the darkest surface. */
  readonly void: string;
  /** Default panel/card surface. */
  readonly surface: string;
  /** Raised surface (hover, nested cards). */
  readonly elevated: string;
  /** Hairline borders and dividers. */
  readonly border: string;
  /** De-emphasized text and icons. */
  readonly muted: string;
  /** Body text. */
  readonly text: string;
  /** Emphasized/heading text. */
  readonly bright: string;
  /** The demo's brand accent. */
  readonly accent: string;
}

/**
 * Build the `--<prefix>-*` custom-property declarations for `scale`.
 *
 * Returned as a `React.CSSProperties` so it can be spread directly into a
 * `style` object (e.g. `MonographDemoShell`'s `surfaceTokens`).
 */
export function monographSurfaceVars(
  prefix: string,
  scale: MonographSurfaceScale,
): React.CSSProperties {
  return {
    [`--${prefix}-void`]: scale.void,
    [`--${prefix}-surface`]: scale.surface,
    [`--${prefix}-elevated`]: scale.elevated,
    [`--${prefix}-border`]: scale.border,
    [`--${prefix}-muted`]: scale.muted,
    [`--${prefix}-text`]: scale.text,
    [`--${prefix}-bright`]: scale.bright,
    [`--${prefix}-accent`]: scale.accent,
  };
}

export type MonographCssTokenSource = Readonly<Record<string, string | undefined>>;

function cssTokenName(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

export function monographTokenVars(
  prefix: string,
  tokens: MonographCssTokenSource,
): React.CSSProperties {
  const vars: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens)) {
    if (value === undefined || value === "") continue;
    vars[`--${prefix}-${cssTokenName(name)}`] = value;
  }
  return vars;
}
